import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, TextField, Autocomplete, Typography, Divider, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import GrainIcon from '@mui/icons-material/Grain';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { getActiveSection } from '../config/menuConfig';
import { supabase } from '../config/supabaseClient';
import { getPendingSyncCount } from '../utils/syncManager';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, ListItemButton, ListItemText, Paper, Button, Tooltip, Menu, MenuItem, Badge, Snackbar, Alert } from '@mui/material';
import { LightMode, DarkMode, Translate as TranslateIcon, CloudSync, CloudDone } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EventIcon from '@mui/icons-material/ConfirmationNumber';
import PublicIcon from '@mui/icons-material/Public';
import DescriptionIcon from '@mui/icons-material/Description';
import TerminalIcon from '@mui/icons-material/Terminal';
import CategoryIcon from '@mui/icons-material/Category';
import SellIcon from '@mui/icons-material/Sell';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import HubIcon from '@mui/icons-material/Hub';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LanguageIcon from '@mui/icons-material/Language';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GetAppIcon from '@mui/icons-material/GetApp';
import MailIcon from '@mui/icons-material/Mail';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { API_BASE_URL } from '../config/api';
import NotificationPopup from './NotificationPopup';

const MainNavbar = ({ sx = {} }) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [weather, setWeather] = useState({ temp: '--', icon: <WbSunnyIcon sx={{ color: '#fbbf24', fontSize: 18 }} /> });
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
  const navigate = useNavigate();
  const location = useLocation();

  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncToastOpen, setSyncToastOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const pulseAnimation = {
    '@keyframes pulse': {
      '0%': { transform: 'scale(1)', color: '#ef4444' },
      '50%': { transform: 'scale(1.2)', color: '#dc2626' },
      '100%': { transform: 'scale(1)', color: '#ef4444' },
    },
    animation: 'pulse 1.5s infinite',
    color: '#ef4444'
  };

  useEffect(() => {
    const updateSyncStatus = async () => {
      try {
        const count = await getPendingSyncCount();
        setPendingSyncCount(count);
      } catch (e) {
        setPendingSyncCount(0);
      }
    };
    updateSyncStatus();
    window.addEventListener('syncStatusChanged', updateSyncStatus);
    return () => window.removeEventListener('syncStatusChanged', updateSyncStatus);
  }, []);

  const handleSyncClick = () => {
    if (pendingSyncCount > 0) {
      setSyncToastOpen(true);
    }
  };

  const activeSection = getActiveSection(location.pathname);
  const subPages = activeSection?.items || [];

  const [dark, setDark] = useState(() => localStorage.getItem('sidebar_dark') === 'true');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navNotifCount, setNavNotifCount] = useState(0);
  const [navDatevIncome, setNavDatevIncome] = useState(0);
  const [navDatevExpense, setNavDatevExpense] = useState(0);
  const [navChatUnread, setNavChatUnread] = useState(0);

  useEffect(() => {
    const handleStats = (e) => {
      setNavNotifCount(e.detail?.notifCount || 0);
      setNavDatevIncome(e.detail?.datevIncome || 0);
      setNavDatevExpense(e.detail?.datevExpense || 0);
      setNavChatUnread(e.detail?.chatUnread || 0);
    };
    window.addEventListener('navStatsUpdated', handleStats);
    return () => window.removeEventListener('navStatsUpdated', handleStats);
  }, []);

  // Tenant switcher removed for isolated single-tenant deployment mode

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('sidebar_dark', String(next));
    // Trigger event for other components (like Sidebar) to update
    window.dispatchEvent(new Event('storage_theme_change'));
  };

  // Sync state if changed elsewhere
  useEffect(() => {
    const sync = () => setDark(localStorage.getItem('sidebar_dark') === 'true');
    window.addEventListener('storage_theme_change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('storage_theme_change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const BG = dark ? '#020617' : '#6B4F3A';
  const TEXT = dark ? '#ffffff' : '#F2E8D8';
  const BORDER = dark ? '#1e293b' : '#6B4F3A';
  const CHIP_BG = dark ? 'rgba(255,255,255,0.05)' : '#f1f5f9';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchWeather = async (lat, lon) => {
      try {
        const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
        const data = await res.json();
        const temp = Math.round(data.current_weather.temperature);
        const code = data.current_weather.weathercode;
        let icon = <WbSunnyIcon sx={{ color: '#fbbf24', fontSize: 18 }} />;
        if (code >= 1 && code <= 3) icon = <CloudIcon sx={{ color: '#94a3b8', fontSize: 18 }} />;
        if (code >= 51 && code <= 67) icon = <GrainIcon sx={{ color: '#38bdf8', fontSize: 18 }} />;
        if (code >= 95) icon = <ThunderstormIcon sx={{ color: '#1e293b', fontSize: 18 }} />;
        setWeather({ temp: String(temp), icon });
      } catch (e) { console.error('Weather fetch failed', e); }
    };
    fetchWeather(47.92, 9.75); // Bad Waldsee fallback
  }, []);

  const fetchSearchOptions = async (q) => {
    if (!q || q.length < 2) { setSearchOptions([]); return; }
    try {
      const res = await fetch('/api/kunden');
      const all = res.ok ? await res.json() : [];
      const lower = q.toLowerCase();
      const matches = (all || []).filter(k =>
        (k.name || k.partner || '').toLowerCase().includes(lower) ||
        (k.kundenId || '').toLowerCase().includes(lower)
      );
      setSearchOptions(matches.slice(0, 10));
    } catch (e) { setSearchOptions([]); }
  };

  const handleSearchChange = (e, val, reason) => {
    const q = typeof val === 'string' ? val : (val && (val.label || val.name || val.partner)) || '';
    if (reason === 'input') fetchSearchOptions(q);
  };

  const handleSearchSelect = (ev, option) => {
    if (!option) return;
    try {
      const targetRoute = option.route || '/verwaltung/kunden';
      if (location.pathname !== targetRoute) {
        navigate(targetRoute, { state: { selectedId: option._id || option.id } });
      } else {
        window.dispatchEvent(new CustomEvent('search.select', { detail: option }));
      }
    } catch {
      window.dispatchEvent(new CustomEvent('search.select', { detail: option }));
    }
  };

  const fullDate = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const weekday = new Date().toLocaleDateString('de-DE', { weekday: 'long' });

  const isStartseite = location.pathname === '/';
  const isSystemHandbuch = location.pathname === '/system-handbuch';

  // --- Startseite Specific Search Logic ---
  const [hubSearchQuery, setHubSearchQuery] = useState('');
  const [hubSearchResults, setHubSearchResults] = useState([]);
  const [hubDbData, setHubDbData] = useState({
    markets: [], products: [], employees: [], tasks: [],
    crmCustomers: [], expenses: [], invoices: []
  });

  useEffect(() => {
    if (isStartseite) {
      const fetchData = async () => {
        try {
          const endpoints = [
            { key: 'markets', url: '/api/markt' },
            { key: 'products', url: '/api/produkte' },
            { key: 'employees', url: '/api/mitarbeiter' },
            { key: 'tasks', url: '/api/kanban' },
            { key: 'crmCustomers', url: '/api/kunden' },
            { key: 'expenses', url: '/api/ausgaben' },
            { key: 'invoices', url: '/api/debitoren' }
          ];
          const data = { ...hubDbData };
          await Promise.all(endpoints.map(async (e) => {
            const r = await fetch(`${API_BASE_URL}${e.url}`);
            if (r.ok) data[e.key] = await r.json();
          }));
          setHubDbData(data);
        } catch (e) { console.error('Hub search data fetch failed', e); }
      };
      fetchData();
    }
  }, [isStartseite]);

  const handleHubSearch = (query) => {
    setHubSearchQuery(query);
    if (!query || query.length < 2) { setHubSearchResults([]); return; }
    const lower = query.toLowerCase();
    const results = [];

    // Search logic replicated from LandingPage
    hubDbData.markets.forEach(m => {
      if ((m.name || '').toLowerCase().includes(lower)) results.push({ title: m.name, subtitle: `Markt · ${m.ort}`, route: '/crm-sales/maerkte', icon: <EventIcon />, type: 'market' });
    });
    hubDbData.products.forEach(p => {
      if ((p.name || '').toLowerCase().includes(lower) || (p.sku || '').toLowerCase().includes(lower)) results.push({ title: p.name, subtitle: `Produkt · ${p.sku}`, route: '/sortiment/produkte', icon: <CategoryIcon />, type: 'product' });
    });
    hubDbData.employees.forEach(e => {
      if ((e.vorname || '').toLowerCase().includes(lower) || (e.nachname || '').toLowerCase().includes(lower)) results.push({ title: `${e.vorname} ${e.nachname}`, subtitle: `Mitarbeiter · ${e.abteilung || ''}`, route: '/verwaltung/mitarbeiter', icon: <GroupsIcon />, type: 'employee' });
    });
    hubDbData.tasks.forEach(t => {
      if ((t.title || '').toLowerCase().includes(lower)) results.push({ title: t.title, subtitle: `Aufgabe · ${t.status}`, route: '/kanban-aufgaben/kanban', icon: <ViewKanbanIcon />, type: 'task' });
    });

    setHubSearchResults(results.slice(0, 8));
  };

  const handleLogout = async () => {
    try {
      // Custom Audit Log trigger
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await fetch(`${API_BASE_URL}/api/audit/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-audit-email': user.email || '' }
      });
      await supabase.auth.signOut();
    } catch (e) { console.error('Logout error:', e); }
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    sessionStorage.removeItem('notifShown');
    navigate('/login');
  };

  const renderRightIcons = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Tooltip title="Mail">
        <IconButton size="small" onClick={() => window.open('https://webmail.strato.de', '_blank')} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <MailIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Chat">
        <IconButton size="small" onClick={() => navigate('/nachrichten/chatroom')} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <Badge badgeContent={navChatUnread} color="error" overlap="circular">
            <WhatsAppIcon fontSize="medium" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="AI">
        <IconButton size="small" onClick={() => navigate('/nachrichten/ai-bot')} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <SmartToyIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title="VBR">
        <IconButton size="small" onClick={() => window.open('https://www.vrbank.de', '_blank')} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <PublicIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title="DATEV">
        <IconButton size="small" onClick={() => navigate('/finanzen-controlling/datev')} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <AccountBalanceIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Backup">
        <IconButton size="small" onClick={() => window.dispatchEvent(new Event('openBackupDialog'))} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <CloudDownloadIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      {/* Divider between App icons and System icons */}
      <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5, borderColor: dark ? '#334155' : '#e2e8f0' }} />

      <Tooltip title="Benachrichtigungen">
        <IconButton id="notif-btn" size="small" onClick={() => setNotifOpen(true)} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <NotificationsIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Daten Export">
        <IconButton size="small" onClick={() => window.dispatchEvent(new Event('openExportDialog'))} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <GetAppIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      
      <Tooltip title={pendingSyncCount > 0 ? `${pendingSyncCount} ausstehende Buchungen` : "Alles synchronisiert"}>
        <IconButton size="small" onClick={handleSyncClick} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <Badge badgeContent={pendingSyncCount} color="error" invisible={pendingSyncCount === 0}>
            {pendingSyncCount > 0 ? <CloudSync fontSize="small" sx={{ color: '#ef4444' }} /> : <CloudDone fontSize="small" sx={{ color: '#10b981' }} />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="Übersetzungsschema">
        <IconButton size="small" sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          <TranslateIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={dark ? "Light Mode" : "Dark Mode"}>
        <IconButton size="small" onClick={toggleDark} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
          {dark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', ml: 1 }}>
        {searchExpanded ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isStartseite ? (
              <Box sx={{ position: 'relative' }}>
                <TextField
                  autoFocus size="small" placeholder="Global suchen..."
                  value={hubSearchQuery} onChange={(e) => handleHubSearch(e.target.value)} autoComplete="off"
                  onBlur={(e) => { if (!hubSearchQuery) setSearchExpanded(false); }}
                  InputProps={{ sx: { borderRadius: 3, width: 250, bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: TEXT, '& fieldset': { border: 'none' }, transition: 'all 0.2s' } }}
                />
                {hubSearchResults.length > 0 && (
                  <Paper elevation={12} sx={{ position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 1000, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', maxHeight: 400, overflowY: 'auto' }}>
                    <Box sx={{ p: 1 }}>
                      {hubSearchResults.map((res, i) => (
                        <ListItemButton key={i} onClick={() => { setHubSearchQuery(''); setHubSearchResults([]); setSearchExpanded(false); navigate(res.route); }} sx={{ borderRadius: 2, gap: 1.5, py: 1 }}>
                          <Avatar sx={{ bgcolor: '#f1f5f9', color: '#64748b', width: 32, height: 32 }}>{res.icon}</Avatar>
                          <ListItemText primary={<Typography fontWeight={800} fontSize="0.85rem">{res.title}</Typography>} secondary={<Typography variant="caption">{res.subtitle}</Typography>} />
                        </ListItemButton>
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            ) : isSystemHandbuch ? (
              <TextField 
                autoFocus placeholder="Handbuch suchen..." size="small"
                onBlur={(e) => { if (!e.target.value) setSearchExpanded(false); }}
                onChange={e => window.dispatchEvent(new CustomEvent('handbuchSearch', { detail: e.target.value }))}
                sx={{ width: 250, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: TEXT, fontSize: 13, '& fieldset': { border: 'none' } } }}
              />
            ) : (
              <Autocomplete
                freeSolo options={searchOptions} getOptionLabel={(opt) => opt.name || opt.partner || opt.kundenId || ''}
                onInputChange={handleSearchChange} onChange={handleSearchSelect}
                onBlur={(e) => { if (!e.target.value) setSearchExpanded(false); }}
                sx={{ width: 250 }}
                renderInput={(params) => (
                  <TextField {...params} autoFocus placeholder="Suche..." size="small"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 3, bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: TEXT, fontSize: 13, '& fieldset': { border: 'none' } } }}
                  />
                )}
              />
            )}
            <IconButton size="small" onClick={() => setSearchExpanded(false)} sx={{ color: TEXT }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Tooltip title="Suchen">
            <IconButton size="small" onClick={() => setSearchExpanded(true)} sx={{ color: dark ? '#94a3b8' : '#C9A227' }}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', position: 'sticky', top: 0, zIndex: 1201, ...sx }}>
      <AppBar position="static" elevation={0} sx={{ background: BG, borderBottom: `1px solid ${BORDER}`, transition: 'background 0.3s' }}>
        <Toolbar disableGutters sx={{ minHeight: 64, px: { xs: 2, md: 3 }, justifyContent: 'space-between', alignItems: 'center' }}>

          {isStartseite ? (
            <Box sx={{ flex: 1 }} />
          ) : (
            <>
              {isSystemHandbuch ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <MenuBookIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={900} sx={{ color: TEXT, letterSpacing: -0.5 }}>System-Handbuch</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, overflow: 'auto', mr: 2 }}>
                  {subPages.map((item) => {
                    const isActive = location.pathname === item.route;
                    return (
                      <Box key={item.route} onClick={() => navigate(item.route)}
                        sx={{
                          px: 2, py: 1, cursor: 'pointer', borderRadius: 1.5,
                          fontSize: 14, fontWeight: isActive ? 700 : 600,
                          color: isActive ? '#C9A227' : 'rgba(255, 255, 255, 0.75)',
                          bgcolor: isActive ? 'rgba(201, 162, 39, 0.1)' : 'transparent',
                          whiteSpace: 'nowrap', transition: 'all 0.2s',
                          '&:hover': { bgcolor: isActive ? 'rgba(201, 162, 39, 0.15)' : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'), color: isActive ? '#C9A227' : TEXT },
                        }}>
                        {item.title}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            {renderRightIcons()}
          </Box>
        </Toolbar>
      </AppBar>

      <Snackbar
        open={syncToastOpen}
        autoHideDuration={6000}
        onClose={() => setSyncToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSyncToastOpen(false)} severity="warning" sx={{ width: '100%', borderRadius: 3, fontWeight: 700 }}>
          {pendingSyncCount} Buchungen warten auf eine Internetverbindung und werden automatisch synchronisiert, sobald du wieder online bist.
        </Alert>
      </Snackbar>

      <NotificationPopup
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
      />
    </Box>
  );
};

export default MainNavbar;